import { getIn, assoc, dissoc } from 'icepick';
import { pickBy } from 'lodash';
import Logger from '@cvent/nucleus-logging';
import { defaultRegistrationTypeId } from 'event-widgets/utils/registrationType';
import { getDefaultAdmissionItemIdSelectionForRegType } from '../../selectors/event';
import { getPrimaryRegistrationId, getEventRegistration } from './selectors';
import querystring from 'querystring';
import { getQueryParam } from '../../../utils/queryUtils';

const LOG = new Logger('redux/registrationForm/regCart/internal');
export const firstEventRegId = '00000000-0000-0000-0000-000000000001';

/**
 * Updates the product registrations for the newly selected admission item. If any sessions were
 * associated to a prior admission item they will be removed. If no admission item is provided it
 * will remove the current admission item if one is present.
 */
export function updateAdmissionItemRegistration(lastSavedRegCart,
  eventRegistrationId, admissionItemId, requestedAction = 'REGISTER') {
  const currentEventReg = getIn(lastSavedRegCart, ['eventRegistrations', eventRegistrationId]);
  const otherProducts = currentEventReg.productRegistrations
    .filter(productReg => productReg.productType !== 'AdmissionItem');

  const selectedOptionalSessions = pickBy(currentEventReg.sessionRegistrations, (session) => {
    const sessionAssociatedToAdmissionItem = session.registrationSourceType === 'AdmissionItem';
    return !sessionAssociatedToAdmissionItem;
  });
  if (admissionItemId) {
    const admissionItemReg = {
      productId: admissionItemId,
      productType: 'AdmissionItem',
      quantity: 1,
      requestedAction
    };
    if (requestedAction === 'UNREGISTER') {
      const otherAdmissionItems = currentEventReg.productRegistrations
        .filter(productReg => productReg.productId !== admissionItemId);
      return {
        productRegistrations: [...otherProducts, ...otherAdmissionItems, admissionItemReg],
        sessionRegistrations: selectedOptionalSessions
      };
    }
    return {
      productRegistrations: [...otherProducts, admissionItemReg],
      sessionRegistrations: selectedOptionalSessions
    };
  }
  return {
    productRegistrations: [...otherProducts],
    sessionRegistrations: selectedOptionalSessions
  };
}

export function updateSessionRegistration(sessionRegistrations, sessionId, action) {
  const sessionItem = {
    productId: sessionId,
    requestedAction: action,
    registrationSourceType: 'Selected'
  };
  const updatedSessionRegistrations = pickBy(sessionRegistrations, (value, key) => key !== sessionId);
  updatedSessionRegistrations[sessionId] = sessionItem;
  return { sessionRegistrations: updatedSessionRegistrations };
}

export function updateSessionWaitlist(sessionWaitlists, sessionId, action) {
  const sessionItem = {
    productId: sessionId,
    requestedAction: action
  };
  const updatedSessionWaitlists = pickBy(sessionWaitlists, (value, key) => key !== sessionId);
  updatedSessionWaitlists[sessionId] = sessionItem;
  return { sessionWaitlists: updatedSessionWaitlists };
}

export function getInitialProductRegistrations(state, registrationTypeId = defaultRegistrationTypeId) {
  const defaultAdmissionItemId = getDefaultAdmissionItemIdSelectionForRegType(state, registrationTypeId);
  if (!defaultAdmissionItemId) {
    // If there is a single admission item it should be selected by default. However, if the users initial registration
    // type cannot pick that admission item we will defer admission item selection to later in registration.
    return [];
  }

  return [{
    productId: defaultAdmissionItemId,
    productType: 'AdmissionItem',
    quantity: 1,
    requestedAction: 'REGISTER'
  }];
}

export async function getAuthenticationByConfirmation(event, registrationForm, accessToken, regCartClient) {
  LOG.debug('confirming existing registration', event, registrationForm);
  const eventRegistration =
  getEventRegistration(registrationForm.regCart,
    getPrimaryRegistrationId(registrationForm.regCart));
  const inviteeId = eventRegistration && eventRegistration.attendee.attendeeId;
  const confirmationNumber = eventRegistration && eventRegistration.confirmationNumber;
  const emailAddress = eventRegistration && eventRegistration.attendee.personalInformation.emailAddress;
  const { accessToken: newAccessToken } = await regCartClient.authorizeByConfirm(
    accessToken, event.id, inviteeId, confirmationNumber, emailAddress);
  return `BEARER ${newAccessToken}`;
}

async function createKnownInviteeRegCart(regCartClient, accessToken, eventId, inviteeId, regTypeId, regPathId) {
  LOG.debug('createRegCartFromLink', inviteeId);
  const response = await regCartClient.createRegCartFromLink(accessToken, inviteeId, regTypeId, regPathId);
  validateInviteeIdForEvent(eventId, response.regCart, inviteeId);
  return response;
}

async function createTestModeRegCart(regCartClient, accessToken, eventId, regTypeId, regPathId) {
  LOG.debug('createTestModeRegCartFromLink', eventId);
  return await regCartClient.createTestModeRegCartFromLink(accessToken, eventId, regTypeId, regPathId);
}

export async function createInitialRegCart(regCartClient, accessToken, state, inviteeId, contactId, eventId,
  isTestMode, referenceId, regTypeId, regPathId, registrationPackId, httpReferrer) {
  // TODO: dholsopple put this back or put behind an experiment or something
  if (false) {
    if (inviteeId) {
      return createKnownInviteeRegCart(regCartClient, accessToken, eventId, inviteeId, regTypeId, regPathId);
    } else if (isTestMode) {
      return createTestModeRegCart(regCartClient, accessToken, eventId, regTypeId, regPathId);
    }
  }

  const queryParams = querystring.parse(window.location.search.slice(1));
  const sms = queryParams.sms || '';
  const cn = queryParams.cn || '';
  const refid = getQueryParam(queryParams, 'refid') || '';
  let socialMediaSourceDetails = {};
  if (sms && !isNaN(sms)) {
    socialMediaSourceDetails.socialMediaSource = parseInt(sms, 10);
  }
  if (cn) {
    socialMediaSourceDetails.invitedByContactStub = cn;
  }
  if (refid) {
    socialMediaSourceDetails.referenceId = refid;
  }

  const initialRegCart = inviteeId ? {} : {
    regPackId: registrationPackId,
    eventRegistrations: {
      [firstEventRegId]: {
        eventId,
        eventRegistrationId: firstEventRegId,
        attendee: {
          personalInformation: {},
          eventAnswers: {},
          referenceId
        },
        productRegistrations: [],
        registrationTypeId: regTypeId,
        registrationPathId: regPathId
      }
    },
    ...(!isEmptyObject(socialMediaSourceDetails) ? { socialMediaSourceDetails } : {}),
    httpReferrer
  };
  LOG.debug('createRegCart', initialRegCart);
  return await regCartClient.createRegCart(accessToken, initialRegCart, contactId, inviteeId);
}

/**
 * Checks if the object passed is empty or not
 * @param {*} object
 */
function isEmptyObject(obj) {
  return Object.keys(obj).length === 0 && obj.constructor === Object;
}

/**
 * Validates if invitee belongs to the event. This should always be the case unless someone is changing URLs by hand.
 */
function validateInviteeIdForEvent(eventId, cart, inviteeId) {
  const eventRegistrationId = Object.keys(cart.eventRegistrations)[0];
  const eventIdFromRegCart = cart.eventRegistrations[eventRegistrationId].eventId;
  if (eventId !== eventIdFromRegCart) {
    throw new Error(
      `The invitee ${inviteeId} does not belong to the event ${eventId}.
      This is probably the result of changing URLs by hand.`
    );
  }
}

/*
This function is used for:
  1) Removing admin info from recart before partial put for product selection
  2) Adding back unsaved admin info to saved regcart after production selection call
*/
export function setAdminRegOnProductionSelection(lastSavedRegCart, unSavedCart) {
  const adminReg = getIn(lastSavedRegCart, ['admin']);
  let newUnSavedCart;
  if (!!adminReg) {
    newUnSavedCart = assoc(unSavedCart, 'admin', adminReg);
  } else {
    newUnSavedCart = dissoc(unSavedCart, 'admin');
  }
  return newUnSavedCart;
}

export function getLastSavedRegCart(getState) {
  const { regCartStatus: { lastSavedRegCart } } = getState();
  return lastSavedRegCart;
}
